import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { AppDialogProvider } from './context/AppDialogContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import TechnicianDashboard from './components/TechnicianDashboard';
import ChangePassword from './components/ChangePassword';
import './App.css';

// Lazy load heavy components for code splitting
const TaskDetails = lazy(() => import('./components/technician/TaskDetails'));
const WorkorderDetails = lazy(() => import('./components/technician/WorkorderDetails'));
const CreateProject = lazy(() => import('./components/admin/CreateProject'));
const ManageTechnicians = lazy(() => import('./components/admin/ManageTechnicians'));
const AssignWorkPackage = lazy(() => import('./components/admin/AssignWorkPackage'));
const TasksDashboard = lazy(() => import('./components/admin/TasksDashboard'));
const CreateTask = lazy(() => import('./components/admin/CreateTask'));
const CreateWorkorder = lazy(() => import('./components/admin/CreateWorkorder'));
const ProjectDetails = lazy(() => import('./components/admin/ProjectDetails'));
const WP1Form = lazy(() => import('./components/WP1Form'));
const DensityReportForm = lazy(() => import('./components/DensityReportForm'));
const RebarForm = lazy(() => import('./components/RebarForm'));
const ProctorForm = lazy(() => import('./components/ProctorForm'));
const ProctorSummary = lazy(() => import('./components/ProctorSummary'));
const Settings = lazy(() => import('./components/admin/Settings'));
const NuclearGauges = lazy(() => import('./components/admin/NuclearGauges'));
const GaugeCheckout = lazy(() => import('./components/GaugeCheckout'));
const ProjectFinancials = lazy(() => import('./components/admin/ProjectFinancials'));
const ProjectSummary = lazy(() => import('./components/admin/ProjectSummary'));
const IntakeQueue = lazy(() => import('./components/admin/IntakeQueue'));
const TechAvailability = lazy(() => import('./components/admin/TechAvailability'));
const Companies = lazy(() => import('./components/admin/Companies'));

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <TenantProvider>
        <Router>
        <AppDialogProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/technician/dashboard"
            element={
              <ProtectedRoute>
                <TechnicianDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/technician/change-password"
            element={
              <ProtectedRoute requireTechnician>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/change-password"
            element={
              <ProtectedRoute requireAdminOrPm>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/technician/task/:id/details"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading task details..." />}>
                  <TaskDetails />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/create-project"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                  <CreateProject />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/technicians"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                  <ManageTechnicians />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assign/:id"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                  <AssignWorkPackage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workpackage/:id/wp1"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading form..." />}>
                  <WP1Form />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tasks"
            element={
              <ProtectedRoute requireAdminOrPm>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading tasks..." />}>
                  <TasksDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/create-task/:projectId"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                  <CreateTask />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task/:taskId/edit"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading..." />}>
                  <CreateTask />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects/:id/details"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading project details..." />}>
                  <ProjectDetails />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects/:id/financials"
            element={
              <ProtectedRoute requireAdminOrPm>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading financials..." />}>
                  <ProjectFinancials />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects/:id/summary"
            element={
              <ProtectedRoute requireAdminOrPm>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading project summary..." />}>
                  <ProjectSummary />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading settings..." />}>
                  <Settings />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task/:id/wp1"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading form..." />}>
                  <WP1Form />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task/:id/density"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading form..." />}>
                  <DensityReportForm />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task/:id/rebar"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading form..." />}>
                  <RebarForm />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task/:id/proctor"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading form..." />}>
                  <ProctorForm />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task/:id/proctor/summary"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading summary..." />}>
                  <ProctorSummary />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/nuclear-gauges"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading gauge log…" />}>
                  <NuclearGauges />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/intake"
            element={
              <ProtectedRoute requireAdminOrPm>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading intake queue…" />}>
                  <IntakeQueue />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/availability"
            element={
              <ProtectedRoute requireAdminOrPm>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading availability…" />}>
                  <TechAvailability />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gauges/:id"
            element={
              <Suspense fallback={<LoadingSpinner fullScreen message="Loading…" />}>
                <GaugeCheckout />
              </Suspense>
            }
          />
          <Route
            path="/technician/workorder/:id"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading workorder…" />}>
                  <WorkorderDetails />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects/:id/create-workorder"
            element={
              <ProtectedRoute requireAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading…" />}>
                  <CreateWorkorder />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute requireAdmin requireSuperAdmin>
                <Suspense fallback={<LoadingSpinner fullScreen message="Loading companies…" />}>
                  <Companies />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/" element={<LandingPage />} />
        </Routes>
        </AppDialogProvider>
        </Router>
        </TenantProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
